import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const KINDS = [
  { key: "like", emoji: "\uD83D\uDC4D", label: "Like" },
  { key: "love", emoji: "\u2764\uFE0F", label: "Love" },
  { key: "star", emoji: "\u2B50", label: "Star" },
];

export default function ReactionBar({ targetType, targetId, initial = {}, size = "md" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({
    like: initial.like || 0,
    love: initial.love || 0,
    star: initial.star || 0,
    my: initial.my || null,
  });
  const [busy, setBusy] = useState(false);

  const toggle = async (kind) => {
    if (!user || user === false) {
      toast.error("Please log in to react");
      nav("/login");
      return;
    }
    if (busy) return;
    setBusy(true);
    // optimistic
    const wasMine = data.my === kind;
    setData((d) => {
      const next = { ...d };
      if (d.my && d.my !== kind) next[d.my] = Math.max(0, (d[d.my] || 0) - 1);
      if (wasMine) {
        next[kind] = Math.max(0, (d[kind] || 0) - 1);
        next.my = null;
      } else {
        next[kind] = (d[kind] || 0) + 1;
        next.my = kind;
      }
      return next;
    });
    try {
      const { data: resp } = await api.post(`/reactions/${targetType}/${targetId}`, { kind });
      if (resp?.reactions) setData(resp.reactions);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const cls =
    size === "sm"
      ? "text-xs px-2 py-1 gap-1"
      : "text-sm px-3 py-1.5 gap-1.5";

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`reactions-${targetType}-${targetId}`}>
      {KINDS.map((k) => {
        const active = data.my === k.key;
        return (
          <button
            key={k.key}
            type="button"
            onClick={() => toggle(k.key)}
            className={`inline-flex items-center rounded-full border transition select-none ${cls} ${
              active
                ? "bg-rose-950/50 border-rose-500/60 text-rose-100 shadow-[0_0_12px_rgba(225,29,72,0.45)]"
                : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10 hover:border-white/20"
            }`}
            aria-pressed={active}
            data-testid={`react-${targetType}-${k.key}`}
            disabled={busy}
          >
            <span aria-hidden>{k.emoji}</span>
            <span className="font-semibold tabular-nums">{data[k.key] || 0}</span>
          </button>
        );
      })}
    </div>
  );
}
